import { MediaPicker } from '@/components/media/MediaPicker';
import type { MediaRow } from '@/routes/media/types';
import { Button, Chip, Input, Select, Spinner } from '@sjuni/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { PublishingSidebar } from '@/components/editor/PublishingSidebar';
import { SlugField } from '@/components/form/SlugField';
import { TranslatableField } from '@/components/form/TranslatableField';
import { TranslationProgress } from '@/components/form/TranslationProgress';
import { type Column, ResourceListScreen } from '@/components/list/ResourceListScreen';
import { useResourceList } from '@/lib/api/resource';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { valueFor } from '@/lib/forms/translatable';
import { useResourceForm } from '@/lib/forms/useResourceForm';

import { BlockEditor } from './blocks/BlockEditor';
import type { PageBlock, PageInput, PageRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/pages`, the generic CMS pages behind
 * `/about/:slug`.
 *
 * §3's asymmetry runs through both halves: a Content Editor opens this list and
 * edits what is in it, and only a Super Admin sees "New page" or a delete
 * button — which `ResourceListScreen` already derives from the permissions, so
 * neither is spelled out here.
 */
export function PagesPage() {
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  return rest === '' ? <List /> : <Editor recordId={rest} />;
}

function List() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();

  const columns: Column<PageRow>[] = [
    {
      key: 'title',
      label: t('pages.columns.title'),
      render: (row) => (
        <div className="min-w-52">
          <span className="font-semibold text-primary">{valueFor(row.title, locale)}</span>

          <span className="mt-0.5 block text-caption text-muted">
            {`/${valueFor(row.slug, locale)}`}
          </span>
        </div>
      ),
    },
    {
      key: 'parent',
      label: t('pages.columns.parent'),
      render: (row) => (
        <span className="text-caption text-secondary">
          {row.parent ? valueFor(row.parent.name, locale) : '—'}
        </span>
      ),
    },
    {
      key: 'blocks',
      label: t('pages.columns.blocks'),
      align: 'end',
      render: (row) => <span className="tabular-nums text-secondary">{row.body.length}</span>,
    },
    {
      key: 'status',
      label: t('content.columns.status'),
      render: (row) => (
        <Chip tone={row.status === 'published' ? 'navy' : 'gold'}>
          {t(`editor.publishing.statuses.${row.status}`)}
        </Chip>
      ),
    },
  ];

  return (
    <ResourceListScreen
      resource="pages"
      title={t('nav.items.pages')}
      intro={t('pages.intro')}
      columns={columns}
      labelFor={(row) => valueFor(row.title, locale)}
      defaultSort="sort_order"
      emptyTitle={t('pages.empty.title')}
      emptyBody={t('pages.empty.body')}
      newLabel={t('pages.new')}
      deleteTitle={t('pages.delete.title')}
      deleteBody={(label) => t('pages.delete.body', { label })}
    />
  );
}

interface Fields {
  title: TranslatedValue;
  slug: TranslatedValue;
  excerpt: TranslatedValue;
  body: PageBlock[];
  cover_media_id: number | null;
  cover: MediaRow | null;
  og_image_media_id: number | null;
  og_image: MediaRow | null;
  template: string;
  show_toc: boolean;
  parent_id: string;
  meta_title: TranslatedValue;
  meta_description: TranslatedValue;
  sort_order: number;
  status: PageRow['status'];
  published_at: string | null;
  pending_review: boolean;
}

function Editor({ recordId }: { recordId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useEditorLocale();
  const { can } = useAuth();

  const mayPublish = can('pages.update');

  // Every page, for the parent selector. The server refuses a parent that would
  // make a loop; this only has to offer the candidates.
  const pages = useResourceList<PageRow>('pages', { per_page: 100, sort: 'sort_order' });

  const { form, values, record, isNew, isLoading, isSubmitting, formError, submit, guard } =
    useResourceForm<PageRow, Fields, PageInput>({
      resource: 'pages',
      recordId,
      empty,
      toFields,
      toInput: (fields) => toInput(fields, mayPublish),
      errorFields: [
        'title',
        'slug',
        'excerpt',
        'body',
        'cover_media_id',
        'og_image_media_id',
        'template',
        'parent_id',
        'meta_title',
        'meta_description',
        'sort_order',
        'status',
        'published_at',
      ],
    });

  const { register, setValue, formState } = form;

  if (isLoading) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-h2 font-extrabold text-primary">
          {isNew ? t('pages.new') : t('pages.edit')}
        </h1>

        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/pages')}>
          {t('common.back')}
        </Button>
      </div>

      <FormError message={formError} />

      <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
        <div className="space-y-5">
          <TranslatableField
            label={t('pages.fields.title')}
            name="title"
            value={values.title}
            onChange={(next) => setValue('title', next, { shouldDirty: true })}
            error={formState.errors.title?.message}
          />

          <SlugField
            value={values.slug}
            onChange={(next) => setValue('slug', next, { shouldDirty: true })}
            original={record?.slug}
            published={values.status === 'published'}
            error={formState.errors.slug?.message}
          />

          <TranslatableField
            label={t('content.fields.summary')}
            hint={t('pages.fields.excerptHint')}
            name="excerpt"
            multiline
            rows={3}
            value={values.excerpt}
            onChange={(next) => setValue('excerpt', next, { shouldDirty: true })}
            required={false}
          />

          {/* §7.1's blocks — the page itself. */}
          <BlockEditor
            value={values.body}
            media={record?.block_media ?? []}
            onChange={(blocks) => setValue('body', blocks, { shouldDirty: true })}
            error={formState.errors.body?.message}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label={t('pages.fields.parent')}
              hint={t('pages.fields.parentHint')}
              {...register('parent_id')}
              error={formState.errors.parent_id?.message}
              options={[
                { value: '', label: t('pages.noParent') },
                ...(pages.data?.data ?? [])
                  // Not itself: the server refuses it, and offering it invites
                  // the refusal.
                  .filter((page) => String(page.id) !== recordId)
                  .map((page) => ({ value: String(page.id), label: valueFor(page.title, locale) })),
              ]}
            />

            <Input
              {...register('template')}
              label={t('pages.fields.template')}
              hint={t('pages.fields.templateHint')}
              error={formState.errors.template?.message}
            />

            <MediaPicker
              label={t('pages.fields.cover')}
              hint={t('staff.fields.photoHint')}
              value={values.cover}
              onChange={(mediaId, media) => {
                setValue('cover_media_id', mediaId, { shouldDirty: true });
                setValue('cover', media, { shouldDirty: true });
              }}
              error={formState.errors.cover_media_id?.message}
            />

            <Input
              {...register('sort_order', { valueAsNumber: true })}
              type="number"
              min={0}
              label={t('content.fields.order')}
              hint={t('content.fields.orderHint')}
            />
          </div>

          <label className="flex items-center gap-2 text-body-sm text-primary">
            <input
              type="checkbox"
              {...register('show_toc')}
              className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
            />
            {t('pages.fields.showToc')}
          </label>

          <fieldset className="space-y-4 rounded-card border border-subtle p-4">
            <legend className="px-1 text-caption font-extrabold tracking-wide text-muted">
              {t('pages.seo')}
            </legend>

            <TranslatableField
              label={t('pages.fields.metaTitle')}
              hint={t('pages.fields.metaTitleHint')}
              name="meta_title"
              value={values.meta_title}
              onChange={(next) => setValue('meta_title', next, { shouldDirty: true })}
              required={false}
            />

            <TranslatableField
              label={t('pages.fields.metaDescription')}
              name="meta_description"
              multiline
              rows={2}
              value={values.meta_description}
              onChange={(next) => setValue('meta_description', next, { shouldDirty: true })}
              required={false}
            />

            <MediaPicker
              label={t('pages.fields.ogImage')}
              value={values.og_image}
              onChange={(mediaId, media) => {
                setValue('og_image_media_id', mediaId, { shouldDirty: true });
                setValue('og_image', media, { shouldDirty: true });
              }}
              error={formState.errors.og_image_media_id?.message}
            />
          </fieldset>

          {/*
            docs/07-MIGRATION.md §5 — where this page came from. Shown so an
            editor comparing against the old site can tell a migrated page from
            one written here.
          */}
          {record?.legacy_url ? (
            <p className="text-caption text-muted">
              {t('pages.migratedFrom', { url: record.legacy_url })}
            </p>
          ) : null}
        </div>

        <div className="space-y-4">
          <PublishingSidebar
            resource="pages"
            status={values.status}
            onStatusChange={(status) => setValue('status', status, { shouldDirty: true })}
            publishedAt={values.published_at}
            onPublishedAtChange={(next) => setValue('published_at', next, { shouldDirty: true })}
            updatedAt={record?.updated_at}
            updatedBy={record?.updated_by}
            pendingReview={record?.pending_review}
            saving={isSubmitting}
          />

          <TranslationProgress
            fields={{
              title: { label: t('pages.fields.title'), value: values.title },
              excerpt: { label: t('content.fields.summary'), value: values.excerpt },
            }}
          />
        </div>
      </div>

      <ConfirmDialog
        open={guard.blocking}
        title={t('editor.unsaved.title')}
        body={t('editor.unsaved.body')}
        confirmLabel={t('editor.unsaved.discard')}
        reversible={false}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </form>
  );
}

function empty(): Fields {
  return {
    title: {},
    slug: {},
    excerpt: {},
    body: [],
    cover_media_id: null,
    cover: null,
    og_image_media_id: null,
    og_image: null,
    template: 'default',
    show_toc: false,
    parent_id: '',
    meta_title: {},
    meta_description: {},
    sort_order: 0,
    status: 'draft',
    published_at: null,
    pending_review: false,
  };
}

function toFields(row: PageRow): Fields {
  return {
    title: row.title,
    slug: row.slug,
    excerpt: row.excerpt,
    // Defensive: a migrated row whose `body` is not an array would otherwise
    // take the block editor down with it.
    body: Array.isArray(row.body) ? row.body : [],
    cover_media_id: row.cover_media_id ?? null,
    cover: row.cover ?? null,
    og_image_media_id: row.og_image_media_id ?? null,
    og_image: row.og_image ?? null,
    template: row.template,
    show_toc: row.show_toc,
    parent_id: row.parent_id === null ? '' : String(row.parent_id),
    meta_title: row.meta_title,
    meta_description: row.meta_description,
    sort_order: row.sort_order,
    status: row.status,
    published_at: row.published_at,
    pending_review: row.pending_review,
  };
}

function toInput(form: Fields, mayPublish: boolean): PageInput {
  return {
    title: form.title,
    slug: form.slug,
    excerpt: form.excerpt,
    body: form.body,
    cover_media_id: form.cover_media_id,
    og_image_media_id: form.og_image_media_id,
    template: form.template.trim() === '' ? 'default' : form.template.trim(),
    show_toc: form.show_toc,
    parent_id: form.parent_id === '' ? null : Number(form.parent_id),
    meta_title: form.meta_title,
    meta_description: form.meta_description,
    sort_order: Number(form.sort_order),
    status: form.status,
    published_at: form.published_at,
    pending_review: mayPublish ? form.pending_review : true,
  };
}
